import { AxiosError } from 'axios';
import { isDeviceOffline } from '../../shared/is-device-offline';

/**
 * Axios error codes for network-level failures, i.e. the request never got a
 * response. These do NOT prove the device is offline: `ERR_NETWORK` in
 * particular is axios's catch-all for CORS blocks, blocked/cancelled requests,
 * and an unreachable server, all of which happen while online.
 */
const CONNECT_FAILURE_AXIOS_CODES = [
  'ERR_NETWORK',
  'ERR_INTERNET_DISCONNECTED',
  'ERR_NAME_NOT_RESOLVED',
];

/**
 * PHI-safe diagnostic fields describing why a dictation transcription request
 * failed. Only codes, HTTP status, and the browser's own online flag — never
 * the request/response bodies (which can carry PHI). Merged into the dictation
 * failure logs so Datadog can tell the failure classes apart.
 */
export type TranscriptionFailureDiagnostics = {
  /**
   *   authentication = reached Heidi, authentication was rejected (401)
   *   connect        = never reached Heidi (network-level, no response)
   *   response       = reached Heidi, it returned another error status (4xx/5xx)
   *   other          = non-axios / otherwise unclassified failure
   */
  failure_class: 'authentication' | 'connect' | 'response' | 'other';
  underlying_error_code?: string;
  http_status?: number;
  /**
   * `navigator.onLine === false` at failure time — DIAGNOSTIC ONLY, it does not
   * change the surfaced error. Attached on connect failures so Datadog can tell
   * a genuinely offline device apart from a can't-reach-Heidi case (host down,
   * DNS/TLS failure, proxy/firewall block) while the device is still online.
   */
  device_reported_offline?: boolean;
};

/**
 * Classify a failed transcription request into PHI-safe diagnostic fields for
 * logging. Shared by the intermediate-chunk path (createRecordingSlice) and the
 * final-chunk path (createDictationSlice).
 *
 * Connection failures surface COULD_NOT_CONNECT_TO_HEIDI; 401 responses surface
 * AUTHENTICATION_REQUIRED. `navigator.onLine` survives purely as the
 * `device_reported_offline` diagnostic above.
 */
export function describeTranscriptionFailure(
  err: unknown
): TranscriptionFailureDiagnostics {
  const isAxios = err instanceof AxiosError;
  const isConnectFailure =
    isAxios && CONNECT_FAILURE_AXIOS_CODES.includes(err.code ?? '');
  const httpStatus = isAxios ? err.response?.status : undefined;

  return {
    failure_class:
      httpStatus === 401
        ? 'authentication'
        : isConnectFailure
          ? 'connect'
          : httpStatus != null
            ? 'response'
            : 'other',
    // Each optional field is attached only when meaningful, so its absence in a
    // log carries information (e.g. no http_status means the request never got
    // a response).
    ...(isAxios && err.code != null && { underlying_error_code: err.code }),
    ...(httpStatus != null && { http_status: httpStatus }),
    ...(isConnectFailure && { device_reported_offline: isDeviceOffline() }),
  };
}
